import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Building2, Landmark, CreditCard, Wallet, TrendingUp, TrendingDown, PiggyBank, DollarSign, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const ACCOUNT_TYPES = [
    { value: 'asset', label: 'Asset', icon: Wallet, color: 'bg-blue-500' },
    { value: 'liability', label: 'Liability', icon: CreditCard, color: 'bg-red-500' },
    { value: 'equity', label: 'Equity', icon: PiggyBank, color: 'bg-purple-500' },
    { value: 'income', label: 'Income', icon: TrendingUp, color: 'bg-green-500' },
    { value: 'expense', label: 'Expense', icon: TrendingDown, color: 'bg-orange-500' },
    { value: 'cogs', label: 'COGS', icon: DollarSign, color: 'bg-yellow-500' },
];

const ACCOUNT_SUB_TYPES = [
    { value: 'bank', label: 'Bank Account' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'cash', label: 'Cash' },
    { value: 'receivable', label: 'Receivable' },
    { value: 'payable', label: 'Payable' },
    { value: 'header', label: 'Header (Parent)' },
    { value: 'prepaid', label: 'Prepaid' },
    { value: 'accrued', label: 'Accrued' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'direct', label: 'Direct' },
];

const ChartOfAccounts = () => {
    const { t } = useLanguage();
    const [allAccounts, setAllAccounts] = useState([]); // For type counts
    const [accounts, setAccounts] = useState([]); // For filtered display
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterSubType, setFilterSubType] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [formData, setFormData] = useState({
        account_code: '',
        account_name: '',
        account_type: 'asset',
        account_sub_type: '',
        description: '',
        is_active: true,
        posting_allowed: true,
        is_bank_account: false,
        bank_name: '',
        bank_account_no: '',
        opening_balance: 0,
        parent_account_id: '',
        sort_order: 0
    });

    // Fetch all accounts once for counts
    useEffect(() => {
        fetchAllAccounts();
    }, []);

    // Fetch filtered accounts when filter changes
    useEffect(() => {
        fetchAccounts();
    }, [filterType, filterSubType, searchTerm]);

    const fetchAllAccounts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/chart-of-accounts`);
            const data = await response.json();
            setAllAccounts(data);
        } catch (error) {
            console.error('Error fetching all accounts:', error);
        }
    };

    const fetchAccounts = async () => {
        try {
            let url = `${API_URL}/api/accounting/chart-of-accounts?`;
            if (filterType) url += `account_type=${filterType}&`;
            if (filterSubType) url += `account_sub_type=${filterSubType}&`;
            if (searchTerm) url += `search=${searchTerm}&`;
            
            const response = await fetch(url);
            const data = await response.json();
            setAccounts(data);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Failed to load accounts');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingAccount 
                ? `${API_URL}/api/accounting/chart-of-accounts/${editingAccount.id}`
                : `${API_URL}/api/accounting/chart-of-accounts`;
            
            const response = await fetch(url, {
                method: editingAccount ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to save account');
            }
            
            toast.success(editingAccount ? 'Account updated!' : 'Account created!');
            setShowModal(false);
            setEditingAccount(null);
            resetForm();
            fetchAccounts();
            fetchAllAccounts(); // Refresh counts
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this account?')) return;
        
        try {
            const response = await fetch(`${API_URL}/api/accounting/chart-of-accounts/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete account');
            }
            
            toast.success('Account deleted!');
            fetchAccounts();
            fetchAllAccounts(); // Refresh counts
        } catch (error) {
            toast.error(error.message);
        }
    };

    const openEditModal = (account) => {
        setEditingAccount(account);
        setFormData({
            account_code: account.account_code || '',
            account_name: account.account_name || '',
            account_type: account.account_type || 'asset',
            account_sub_type: account.account_sub_type || '',
            description: account.description || '',
            is_active: account.is_active ?? true,
            posting_allowed: account.posting_allowed ?? true,
            is_bank_account: account.is_bank_account || false,
            bank_name: account.bank_name || '',
            bank_account_no: account.bank_account_no || '',
            opening_balance: account.opening_balance || 0,
            parent_account_id: account.parent_account_id || '',
            sort_order: account.sort_order || 0
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            account_code: '',
            account_name: '',
            account_type: 'asset',
            account_sub_type: '',
            description: '',
            is_active: true,
            posting_allowed: true,
            is_bank_account: false,
            bank_name: '',
            bank_account_no: '',
            opening_balance: 0,
            parent_account_id: '',
            sort_order: 0
        });
    };

    const getTypeCounts = () => {
        const counts = {};
        const balances = {};
        ACCOUNT_TYPES.forEach(type => {
            counts[type.value] = allAccounts.filter(a => a.account_type === type.value).length;
            balances[type.value] = allAccounts
                .filter(a => a.account_type === type.value)
                .reduce((sum, a) => sum + (a.current_balance || 0), 0);
        });
        return { counts, balances };
    };

    const { counts, balances } = getTypeCounts();
    const bankAccounts = allAccounts.filter(a => a.is_bank_account);
    const creditCards = allAccounts.filter(a => a.account_sub_type === 'credit_card');

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { 
            style: 'currency', 
            currency: 'USD',
            minimumFractionDigits: 2 
        }).format(amount || 0);
    };

    const getTypeInfo = (type) => ACCOUNT_TYPES.find(t => t.value === type) || ACCOUNT_TYPES[0];

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Building2 className="w-7 h-7 text-blue-500" />
                        {t('chartOfAccounts')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('manageGLAccounts')}
                    </p>
                </div>
                <Button 
                    onClick={() => { resetForm(); setEditingAccount(null); setShowModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="add-account-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addAccount')}
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                {ACCOUNT_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                        <div 
                            key={type.value}
                            className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                                filterType === type.value ? 'ring-2 ring-blue-500' : ''
                            }`}
                            style={{ borderLeftColor: type.color.replace('bg-', '#').replace('-500', '') }}
                            onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className={`w-8 h-8 rounded-lg ${type.color} bg-opacity-20 flex items-center justify-center`}>
                                    <Icon className={`w-4 h-4 ${type.color.replace('bg-', 'text-')}`} />
                                </div>
                                <span className="text-lg font-bold text-slate-900 dark:text-white">{counts[type.value]}</span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{type.label}</p>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">
                                {formatCurrency(balances[type.value])}
                            </p>
                        </div>
                    );
                })}
                
                {/* Bank Accounts Card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-cyan-500">
                    <div className="flex items-center justify-between mb-2">
                        <div className="w-8 h-8 rounded-lg bg-cyan-500 bg-opacity-20 flex items-center justify-center">
                            <Landmark className="w-4 h-4 text-cyan-500" />
                        </div>
                        <span className="text-lg font-bold text-slate-900 dark:text-white">{bankAccounts.length}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Banks</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">
                        {formatCurrency(bankAccounts.reduce((sum, a) => sum + (a.current_balance || 0), 0))}
                    </p>
                </div>
                
                {/* Credit Cards Card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-pink-500">
                    <div className="flex items-center justify-between mb-2">
                        <div className="w-8 h-8 rounded-lg bg-pink-500 bg-opacity-20 flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-pink-500" />
                        </div>
                        <span className="text-lg font-bold text-slate-900 dark:text-white">{creditCards.length}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Credit Cards</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">
                        {formatCurrency(creditCards.reduce((sum, a) => sum + (a.current_balance || 0), 0))}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder={t('searchAccounts')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-accounts"
                        />
                    </div>
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="filter-type"
                >
                    <option value="">{t('allTypes')}</option>
                    {ACCOUNT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </select>
                <select
                    value={filterSubType}
                    onChange={(e) => setFilterSubType(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="filter-subtype"
                >
                    <option value="">{t('allSubTypes')}</option>
                    {ACCOUNT_SUB_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </select>
            </div>

            {/* Accounts Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-100 dark:bg-slate-700">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Code</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Account Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Sub-Type</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Balance</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Status</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                                        {t('loading')}
                                    </td>
                                </tr>
                            ) : accounts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                                        {t('noAccountsFound')}
                                    </td>
                                </tr>
                            ) : (
                                accounts.map((account) => {
                                    const typeInfo = getTypeInfo(account.account_type);
                                    const isHeader = account.account_sub_type === 'header';
                                    return (
                                        <tr 
                                            key={account.id} 
                                            className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                                                isHeader ? 'bg-slate-50 dark:bg-slate-800/50 font-semibold' : ''
                                            }`}
                                        >
                                            <td className="px-4 py-3 text-sm font-mono text-slate-900 dark:text-white">
                                                {account.account_code}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {account.is_bank_account && (
                                                        <Landmark className="w-4 h-4 text-cyan-500" />
                                                    )}
                                                    <span className="text-sm text-slate-900 dark:text-white">
                                                        {account.account_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} bg-opacity-20 ${typeInfo.color.replace('bg-', 'text-')}`}>
                                                    {typeInfo.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                {account.account_sub_type ? 
                                                    ACCOUNT_SUB_TYPES.find(s => s.value === account.account_sub_type)?.label || account.account_sub_type
                                                    : '-'
                                                }
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-medium text-slate-900 dark:text-white">
                                                {formatCurrency(account.current_balance)}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                                    account.is_active 
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                }`}>
                                                    {account.is_active ? t('active') : t('inactive')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(account)}
                                                        className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(account.id)}
                                                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                {editingAccount ? t('editAccount') : t('addAccount')}
                            </h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        {t('accountCode')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData.account_code}
                                        onChange={(e) => setFormData({...formData, account_code: e.target.value})}
                                        required
                                        placeholder="e.g., 1001"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        {t('accountName')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData.account_name}
                                        onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                                        required
                                        placeholder="e.g., Cash in Bank"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        {t('accountType')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.account_type}
                                        onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                        required
                                    >
                                        {ACCOUNT_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        {t('subType')}
                                    </label>
                                    <select
                                        value={formData.account_sub_type}
                                        onChange={(e) => setFormData({...formData, account_sub_type: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    >
                                        <option value="">Select...</option>
                                        {ACCOUNT_SUB_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    {t('description')}
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    rows={2}
                                    placeholder="Optional description..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        {t('openingBalance')}
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={formData.opening_balance}
                                        onChange={(e) => setFormData({...formData, opening_balance: parseFloat(e.target.value) || 0})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Sort Order
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.sort_order}
                                        onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Active</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.posting_allowed}
                                        onChange={(e) => setFormData({...formData, posting_allowed: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Allow Posting</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_bank_account}
                                        onChange={(e) => setFormData({...formData, is_bank_account: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Bank Account</span>
                                </label>
                            </div>

                            {formData.is_bank_account && (
                                <div className="grid grid-cols-2 gap-4 p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Bank Name
                                        </label>
                                        <Input
                                            value={formData.bank_name}
                                            onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                                            placeholder="e.g., Chase Bank"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Account Number
                                        </label>
                                        <Input
                                            value={formData.bank_account_no}
                                            onChange={(e) => setFormData({...formData, bank_account_no: e.target.value})}
                                            placeholder="e.g., ****1234"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => { setShowModal(false); setEditingAccount(null); }}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                                    {editingAccount ? t('update') : t('create')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChartOfAccounts;
